import React, { useEffect, useMemo, useState } from "react";
import {
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
    faInfoCircle,
    faClock,
    faCopy,
    faEraser,
    faEye,
    faFilter,
    faHistory,
    faSearch,
    faTable,
    faTimes,
    faUserClock,
    faFileExcel,
    faExternalLinkAlt,
    faLayerGroup,
    faShieldAlt,
    faFileExport,
    faPeopleArrows
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";

import "./ActionHistory.css";
import { search } from "../../store/api/ActionHistoryApi";


const normalize = (payload) => {
    if (Array.isArray(payload)) {
        return {
            items: payload
        };
    }

    return {
        items: payload?.items || payload?.data || payload?.result || [],
        total: payload?.total,
        page: payload?.page,
        pageSize: payload?.pageSize,
        stats: payload?.stats
    };
};


const val = (v) => {
    return v === null ||
        v === undefined ||
        v === "" ||
        String(v).toUpperCase() === "NULL"
        ? "—"
        : String(v);
};


const dateObj = (v) => {
    const d = new Date(v);

    return Number.isNaN(d.getTime())
        ? null
        : d;
};


const formatDate = (v) => {
    const d = dateObj(v);

    return d
        ? d.toLocaleString("fa-IR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
        })
        : val(v);
};


const formatDay = (v) => {
    const d = dateObj(v);

    return d
        ? d.toLocaleDateString("fa-IR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        })
        : "—";
};


const sameDay = (a, b) => {
    const x = dateObj(a);
    const y = dateObj(b);

    return x &&
        y &&
        x.toDateString() === y.toDateString();
};


const eventKey = (r) => {
    return [
        r.TableName || "",
        val(r.KeyColumnValue),
        r.ActionDate || ""
    ].join("|");
};


function classify(c) {
    const oldValue = String(c.OldValue ?? "").toUpperCase();
    const newValue = String(c.NewValue ?? "").toUpperCase();

    if ((oldValue === "NULL" || oldValue === "") && newValue !== "") {
        return "create";
    }

    if ((newValue === "NULL" || newValue === "") && oldValue !== "") {
        return "delete";
    }

    if (newValue === "TRUE" && oldValue === "FALSE") {
        return "enable";
    }

    if (newValue === "FALSE" && oldValue === "TRUE") {
        return "disable";
    }

    return "update";
}


const typeMap = {
    create: ["جدید", "success"],
    delete: ["حذف", "danger"],
    enable: ["فعال", "success"],
    disable: ["غیرفعال", "warning"],
    update: ["ویرایش", "info"]
};


function ChangeBadge({ change }) {
    const [label, variant] =
        typeMap[classify(change)] || typeMap.update;

    return (
        <Badge pill variant={variant}>
            {label}
        </Badge>
    );
}


function groupEvents(rows) {
    const map = new Map();

    rows.forEach((r, i) => {
        const key = eventKey(r);

        if (!map.has(key)) {
            map.set(key, {
                id: key,
                TableName: r.TableName,
                KeyColumnValue: r.KeyColumnValue,
                ActionDate: r.ActionDate,
                changes: []
            });
        }

        map.get(key).changes.push({
            ...r,
            _index: i
        });
    });

    return Array.from(map.values()).sort(
        (a, b) =>
            (dateObj(b.ActionDate)?.getTime() || 0) -
            (dateObj(a.ActionDate)?.getTime() || 0)
    );
}


function Stat({
    icon,
    title,
    value,
    detail,
    kind
}) {
    return (
        <Card className={`bht-stat bht-stat-${kind || "default"}`}>
            <div className="bht-stat-icon">
                <FontAwesomeIcon icon={icon} />
            </div>

            <div className="bht-stat-copy">
                <span>{title}</span>

                <strong>
                    {Number(value || 0).toLocaleString("fa-IR")}
                </strong>

                {detail && (
                    <small>{detail}</small>
                )}
            </div>
        </Card>
    );
}


export default function ActionHistory({
    apiUrl = "/api/action-history",
    exportApiUrl = "",
    initialData = [],
    useMock = false,
    serverSide = false,
    pageSize: defaultPageSize = 25,
    title = "تاریخچه عملیات بی‌همتا",
    subtitle = "تاریخچه تغییرات، عملیات و ردپای اطلاعات",
    entityResolver,
    onOpenRecord,
    tableLabels = {},
    initialEntity = null
}) {
    const [rows, setRows] = useState(initialData || []);
    const [total, setTotal] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [query, setQuery] = useState("");
    const [table, setTable] = useState("");
    const [column, setColumn] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [selected, setSelected] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [compact, setCompact] = useState(false);
    const [sort, setSort] = useState("newest");
    const [scope, setScope] = useState(
        initialEntity ? "record" : "all"
    );
    const [copied, setCopied] = useState(false);

    const effectiveEntity = initialEntity;


    const buildParams = (extra = {}) => {
        const p = new URLSearchParams();

        if (query.trim()) {
            p.set("search", query.trim());
        }

        if (table) {
            p.set("tableName", table);
        }

        if (column) {
            p.set("columnName", column);
        }

        if (from) {
            p.set("from", from);
        }

        if (to) {
            p.set("to", to);
        }

        p.set("sort", sort);
        p.set("page", String(page));
        p.set("pageSize", String(pageSize));

        if (scope === "record" && effectiveEntity) {
            p.set(
                "entityTable",
                effectiveEntity.table || ""
            );

            p.set(
                "entityId",
                effectiveEntity.id || ""
            );
        }

        Object.entries(extra).forEach(([key, value]) => {
            if (
                value !== undefined &&
                value !== null &&
                value !== ""
            ) {
                p.set(key, String(value));
            }
        });

        return p;
    };


    const load = async () => {
        if (useMock && !serverSide) {
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await search();

            if (response.Message) {
                setError(response.Message);
            }

            const payload = normalize(response.Data);

            setRows(payload.items || []);

            if (payload.total !== undefined) {
                setTotal(payload.total);
            }

            if (payload.stats) {
                setStats(payload.stats);
            }
        } catch (e) {
            setError(
                "دریافت لاگ از سرور انجام نشد. آدرس API، احراز هویت و دسترسی کاربر را بررسی کنید."
            );
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (serverSide || !useMock) {
            load();
        }
    }, [
        apiUrl,
        serverSide,
        useMock,
        page,
        pageSize,
        query,
        table,
        column,
        from,
        to,
        sort,
        scope,
        effectiveEntity?.table,
        effectiveEntity?.id
    ]);


    const tables = useMemo(
        () =>
            [
                ...new Set(
                    rows
                        .map((x) => x.TableName)
                        .filter(Boolean)
                )
            ].sort(),
        [rows]
    );


    const columns = useMemo(
        () =>
            [
                ...new Set(
                    rows
                        .map((x) => x.ColumnName)
                        .filter(Boolean)
                )
            ].sort(),
        [rows]
    );


    const filtered = useMemo(() => {
        if (serverSide) {
            return rows;
        }

        const q = query.trim().toLowerCase();

        return rows.filter((r) => {
            const hay = [
                r.TableName,
                r.KeyColumnValue,
                r.ColumnName,
                r.OldValue,
                r.NewValue
            ]
                .map(val)
                .join(" ")
                .toLowerCase();

            const d = dateObj(r.ActionDate);

            return (
                (!q || hay.includes(q)) &&
                (!table || r.TableName === table) &&
                (!column || r.ColumnName === column) &&
                (!from ||
                    (d &&
                        d >= new Date(
                            `${from}T00:00:00`
                        ))) &&
                (!to ||
                    (d &&
                        d <= new Date(
                            `${to}T23:59:59`
                        ))) &&
                (!(
                    scope === "record" &&
                    effectiveEntity
                ) ||
                    (
                        r.TableName ===
                            effectiveEntity.table &&
                        String(r.KeyColumnValue) ===
                            String(effectiveEntity.id)
                    ))
            );
        });
    }, [
        rows,
        query,
        table,
        column,
        from,
        to,
        scope,
        effectiveEntity,
        serverSide
    ]);


    const events = useMemo(() => {
        const grouped = groupEvents(filtered);

        if (sort === "oldest") {
            grouped.reverse();
        }

        return grouped;
    }, [filtered, sort]);


    const pages = serverSide
        ? Math.max(
            1,
            Math.ceil(
                Number(total || 0) / pageSize
            )
        )
        : Math.max(
            1,
            Math.ceil(
                events.length / pageSize
            )
        );


    const shown = serverSide
        ? events
        : events.slice(
            (page - 1) * pageSize,
            page * pageSize
        );


    useEffect(() => {
        if (page > pages) {
            setPage(pages);
        }
    }, [page, pages]);


    const activeFilters = [
        table && {
            label: "جدول",
            value: table,
            clear: () => setTable("")
        },

        column && {
            label: "فیلد",
            value: column,
            clear: () => setColumn("")
        },

        from && {
            label: "از",
            value: from,
            clear: () => setFrom("")
        },

        to && {
            label: "تا",
            value: to,
            clear: () => setTo("")
        },

        scope === "record" &&
            effectiveEntity && {
                label: "رکورد",
                value: `${effectiveEntity.table} #${effectiveEntity.id}`,
                clear: () => setScope("all")
            }
    ].filter(Boolean);


    const rawCount =
        stats?.changes ??
        (
            serverSide
                ? total || rows.length
                : rows.length
        );


    const eventCount =
        stats?.events ??
        (
            serverSide
                ? Math.min(
                    total || 0,
                    events.length
                )
                : events.length
        );


    const tablesCount =
        stats?.tables ??
        new Set(
            filtered
                .map((r) => r.TableName)
                .filter(Boolean)
        ).size;


    const todayCount =
        stats?.today ??
        rows.filter((r) =>
            sameDay(r.ActionDate, new Date())
        ).length;


    const clearAll = () => {
        setQuery("");
        setTable("");
        setColumn("");
        setFrom("");
        setTo("");
        setScope("all");
        setPage(1);
    };


    const copyId = async (id) => {
        try {
            await navigator.clipboard.writeText(
                String(id)
            );

            setCopied(true);

            setTimeout(
                () => setCopied(false),
                1200
            );
        } catch (e) {
        }
    };


    const exportExcel = () => {
        if (exportApiUrl) {
            window.open(
                `${exportApiUrl}${
                    exportApiUrl.includes("?")
                        ? "&"
                        : "?"
                }${buildParams({
                    page: 1,
                    pageSize: 1000000
                }).toString()}`,
                "_blank"
            );

            return;
        }

        const data = filtered.map((r) => ({
            "جدول": r.TableName,
            "شناسه رکورد": val(r.KeyColumnValue),
            "تاریخ و زمان": r.ActionDate,
            "فیلد": r.ColumnName,
            "مقدار قبلی": val(r.OldValue),
            "مقدار جدید": val(r.NewValue)
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            wb,
            ws,
            "AuditLog"
        );

        XLSX.writeFile(
            wb,
            `BiHamta-AuditLog-${new Date()
                .toISOString()
                .slice(0, 10)}.xlsx`
        );
    };


    const entityInfo =
        selected && entityResolver
            ? entityResolver(
                selected.TableName,
                selected.KeyColumnValue
            )
            : null;


    return (
        <div
            className={`bht-log ${
                compact ? "bht-compact" : ""
            }`}
            dir="rtl"
        >
            <div className="bht-hero">
                <div className="bht-brand-row">
                    <div className="bht-hero-icon">
                        <FontAwesomeIcon
                            icon={faShieldAlt}
                        />
                    </div>

                    <div>
                        <h1>{title}</h1>
                        <p>{subtitle}</p>
                    </div>
                </div>

                <div className="bht-actions">
                    <Button
                        variant="light"
                        onClick={() =>
                            setShowFilters((v) => !v)
                        }
                    >
                        <FontAwesomeIcon
                            icon={faFilter}
                        />{" "}
                        فیلترها{" "}
                        {activeFilters.length > 0 && (
                            <Badge variant="primary">
                                {activeFilters.length}
                            </Badge>
                        )}
                    </Button>

                    <Button
                        variant="light"
                        onClick={() =>
                            setCompact((v) => !v)
                        }
                    >
                        {compact
                            ? "نمایش راحت"
                            : "نمایش فشرده"}
                    </Button>

                    <Button
                        variant="light"
                        onClick={load}
                        disabled={loading}
                    >
                        <FontAwesomeIcon
                            icon={faPeopleArrows}
                            spin={loading}
                        />{" "}
                        بروزرسانی
                    </Button>

                    <Button
                        variant="primary"
                        onClick={exportExcel}
                    >
                        <FontAwesomeIcon
                            icon={faFileExport}
                        />{" "}
                        خروجی
                    </Button>
                </div>
            </div>


            {effectiveEntity && (
                <div className="bht-context">
                    <div>
                        <span>در حال مشاهده</span>

                        <strong>
                            {entityInfo?.label ||
                                tableLabels[
                                    effectiveEntity.table
                                ] ||
                                effectiveEntity.table}
                        </strong>

                        <small>
                            شناسه: {effectiveEntity.id}
                        </small>
                    </div>

                    <div className="bht-context-actions">
                        <Button
                            size="sm"
                            variant={
                                scope === "record"
                                    ? "primary"
                                    : "outline-primary"
                            }
                            onClick={() => {
                                setScope("record");
                                setPage(1);
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faClock}
                            />{" "}
                            تاریخچه همین رکورد
                        </Button>

                        {onOpenRecord && (
                            <Button
                                size="sm"
                                variant="light"
                                onClick={() =>
                                    onOpenRecord(
                                        effectiveEntity.table,
                                        effectiveEntity.id
                                    )
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faExternalLinkAlt}
                                />{" "}
                                باز کردن رکورد
                            </Button>
                        )}
                    </div>
                </div>
            )}


            <Row className="bht-stats">
                <Col lg={3} md={6}>
                    <Stat
                        icon={faHistory}
                        title="تغییرات"
                        value={rawCount}
                        detail="رکوردهای لاگ"
                        kind="blue"
                    />
                </Col>

                <Col lg={3} md={6}>
                    <Stat
                        icon={faLayerGroup}
                        title="رویدادها"
                        value={eventCount}
                        detail="عملیات گروه‌بندی‌شده"
                        kind="violet"
                    />
                </Col>

                <Col lg={3} md={6}>
                    <Stat
                        icon={faTable}
                        title="جداول"
                        value={tablesCount}
                        detail={`${columns.length.toLocaleString(
                            "fa-IR"
                        )} فیلد در نتیجه`}
                        kind="green"
                    />
                </Col>

                <Col lg={3} md={6}>
                    <Stat
                        icon={faUserClock}
                        title="امروز"
                        value={todayCount}
                        detail="تغییرات ثبت‌شده امروز"
                        kind="orange"
                    />
                </Col>
            </Row>


            <Card className="bht-panel">
                <Card.Body>
                    <div className="bht-toolbar">
                        <div className="bht-search">
                            <FontAwesomeIcon
                                icon={faSearch}
                            />

                            <input
                                value={query}
                                onChange={(e) => {
                                    setQuery(
                                        e.target.value
                                    );
                                    setPage(1);
                                }}
                                placeholder="جستجو در جدول، شناسه، فیلد یا مقدار..."
                            />

                            <button
                                type="button"
                                onClick={() =>
                                    setQuery("")
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faFileExcel}
                                />
                            </button>
                        </div>

                        <div className="bht-toolbar-meta">
                            {serverSide
                                ? "Server-side"
                                : `${filtered.length.toLocaleString(
                                    "fa-IR"
                                )} نتیجه`}

                            <span className="dot" />

                            صفحه{" "}
                            {page.toLocaleString("fa-IR")}{" "}
                            از{" "}
                            {pages.toLocaleString(
                                "fa-IR"
                            )}
                        </div>
                    </div>


                    {showFilters && (
                        <div className="bht-filter-box">
                            <div className="bht-filter-head">
                                <div>
                                    <strong>
                                        فیلترهای پیشرفته
                                    </strong>

                                    <small>
                                        برای لاگ‌های بزرگ،
                                        فیلترها مستقیماً به
                                        سرور ارسال می‌شوند.
                                    </small>
                                </div>

                                <Button
                                    size="sm"
                                    variant="link"
                                    onClick={clearAll}
                                >
                                    <FontAwesomeIcon
                                        icon={faEraser}
                                    />{" "}
                                    پاک کردن همه
                                </Button>
                            </div>


                            <Row>
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>
                                            جدول
                                        </Form.Label>

                                        <Form.Control
                                            as="select"
                                            value={table}
                                            onChange={(e) => {
                                                setTable(
                                                    e.target
                                                        .value
                                                );
                                                setPage(1);
                                            }}
                                        >
                                            <option value="">
                                                همه جداول
                                            </option>

                                            {tables.map(
                                                (t) => (
                                                    <option
                                                        key={t}
                                                    >
                                                        {t}
                                                    </option>
                                                )
                                            )}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>


                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>
                                            فیلد
                                        </Form.Label>

                                        <Form.Control
                                            as="select"
                                            value={column}
                                            onChange={(e) => {
                                                setColumn(
                                                    e.target
                                                        .value
                                                );
                                                setPage(1);
                                            }}
                                        >
                                            <option value="">
                                                همه فیلدها
                                            </option>

                                            {columns.map(
                                                (c) => (
                                                    <option
                                                        key={c}
                                                    >
                                                        {c}
                                                    </option>
                                                )
                                            )}
                                        </Form.Control>
                                    </Form.Group>
                                </Col>


                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>
                                            از تاریخ
                                        </Form.Label>

                                        <Form.Control
                                            type="date"
                                            value={from}
                                            onChange={(e) => {
                                                setFrom(
                                                    e.target
                                                        .value
                                                );
                                                setPage(1);
                                            }}
                                        />
                                    </Form.Group>
                                </Col>


                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>
                                            تا تاریخ
                                        </Form.Label>

                                        <Form.Control
                                            type="date"
                                            value={to}
                                            onChange={(e) => {
                                                setTo(
                                                    e.target
                                                        .value
                                                );
                                                setPage(1);
                                            }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>


                            <div className="bht-chips">
                                {activeFilters.map(
                                    (f, i) => (
                                        <button
                                            key={i}
                                            onClick={f.clear}
                                        >
                                            {f.label}:{" "}
                                            {f.value}{" "}
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                            />
                                        </button>
                                    )
                                )}
                            </div>
                        </div>
                    )}


                    {error && (
                        <div className="bht-error">
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                            />
                            {error}
                        </div>
                    )}


                    {loading ? (
                        <div className="bht-loading">
                            <Spinner
                                animation="border"
                                size="sm"
                            />{" "}
                            در حال دریافت تاریخچه...
                        </div>
                    ) : shown.length === 0 ? (
                        <div className="bht-empty">
                            <div>
                                <FontAwesomeIcon
                                    icon={faSearch}
                                />
                            </div>

                            <strong>
                                موردی پیدا نشد
                            </strong>

                            <span>
                                فیلترها یا عبارت جستجو را
                                تغییر دهید.
                            </span>

                            <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={clearAll}
                            >
                                حذف فیلترها
                            </Button>
                        </div>
                    ) : (
                        <div className="bht-event-list">
                            {shown.map((event) => (
                                <div
                                    className="bht-event"
                                    key={event.id}
                                >
                                    <div className="bht-event-time">
                                        <strong>
                                            {formatDay(
                                                event.ActionDate
                                            )}
                                        </strong>

                                        <span>
                                            {dateObj(
                                                event.ActionDate
                                            )?.toLocaleTimeString(
                                                "fa-IR",
                                                {
                                                    hour: "2-digit",
                                                    minute: "2-digit"
                                                }
                                            )}
                                        </span>
                                    </div>


                                    <div className="bht-event-marker">
                                        <span />
                                    </div>


                                    <div>
                                        <div className="bht-event-top">
                                            <div className="bht-event-title">
                                                <strong>
                                                    {tableLabels[
                                                        event
                                                            .TableName
                                                    ] ||
                                                        event.TableName}
                                                </strong>

                                                <span className="bht-key">
                                                    شناسه:{" "}
                                                    <b>
                                                        {val(
                                                            event.KeyColumnValue
                                                        )}
                                                    </b>
                                                </span>
                                            </div>

                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                onClick={() =>
                                                    setSelected(
                                                        event
                                                    )
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faEye}
                                                />{" "}
                                                جزئیات
                                            </Button>
                                        </div>


                                        <div className="bht-change-grid">
                                            {event.changes
                                                .slice(
                                                    0,
                                                    compact
                                                        ? 2
                                                        : 4
                                                )
                                                .map((c) => (
                                                    <div
                                                        className="bht-change"
                                                        key={
                                                            c._index
                                                        }
                                                    >
                                                        <div className="bht-change-head">
                                                            <strong>
                                                                {
                                                                    c.ColumnName
                                                                }
                                                            </strong>

                                                            <ChangeBadge
                                                                change={
                                                                    c
                                                                }
                                                            />
                                                        </div>

                                                        <div className="bht-values">
                                                            <span className="old">
                                                                {val(
                                                                    c.OldValue
                                                                )}
                                                            </span>

                                                            <span className="arrow">
                                                                →
                                                            </span>

                                                            <span className="new">
                                                                {val(
                                                                    c.NewValue
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}


                                            {event.changes.length >
                                                (compact
                                                    ? 2
                                                    : 4) && (
                                                <button
                                                    className="bht-more"
                                                    onClick={() =>
                                                        setSelected(
                                                            event
                                                        )
                                                    }
                                                >
                                                    +{" "}
                                                    {(
                                                        event
                                                            .changes
                                                            .length -
                                                        (compact
                                                            ? 2
                                                            : 4)
                                                    ).toLocaleString(
                                                        "fa-IR"
                                                    )}{" "}
                                                    تغییر دیگر
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}


                    <div className="bht-footer">
                        <span>
                            {serverSide
                                ? `${Number(
                                    total || 0
                                ).toLocaleString(
                                    "fa-IR"
                                )} نتیجه در سرور`
                                : `${filtered.length.toLocaleString(
                                    "fa-IR"
                                )} تغییر در این فیلتر`}
                        </span>

                        <div className="bht-pagination">
                            <Form.Control
                                as="select"
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(
                                        Number(
                                            e.target.value
                                        )
                                    );
                                    setPage(1);
                                }}
                            >
                                <option value="25">
                                    ۲۵ / صفحه
                                </option>

                                <option value="50">
                                    ۵۰ / صفحه
                                </option>

                                <option value="100">
                                    ۱۰۰ / صفحه
                                </option>
                            </Form.Control>

                            <Button
                                variant="light"
                                disabled={page <= 1}
                                onClick={() =>
                                    setPage(
                                        (p) => p - 1
                                    )
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faChevronRight}
                                />
                            </Button>

                            <span>
                                {page.toLocaleString(
                                    "fa-IR"
                                )}
                            </span>

                            <Button
                                variant="light"
                                disabled={page >= pages}
                                onClick={() =>
                                    setPage(
                                        (p) => p + 1
                                    )
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faChevronLeft}
                                />
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>


            <Modal
                show={!!selected}
                onHide={() => setSelected(null)}
                size="xl"
                centered
                className="bht-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon
                            icon={faInfoCircle}
                        />{" "}
                        جزئیات عملیات
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {selected && (
                        <>
                            <div className="bht-detail-head">
                                <div>
                                    <span>
                                        {tableLabels[
                                            selected.TableName
                                        ] ||
                                            selected.TableName}
                                    </span>

                                    <h3>
                                        {selected.TableName}
                                    </h3>

                                    <small>
                                        {formatDate(
                                            selected.ActionDate
                                        )}
                                    </small>
                                </div>

                                <div className="bht-detail-key">
                                    <div>
                                        <small>
                                            شناسه رکورد
                                        </small>

                                        <strong>
                                            {val(
                                                selected.KeyColumnValue
                                            )}
                                        </strong>
                                    </div>

                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() =>
                                            copyId(
                                                selected.KeyColumnValue
                                            )
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={faCopy}
                                        />{" "}
                                        {copied
                                            ? "کپی شد"
                                            : "کپی"}
                                    </Button>
                                </div>
                            </div>


                            <div className="bht-detail-table">
                                {selected.changes.map(
                                    (c) => (
                                        <div
                                            className="bht-detail-row"
                                            key={c._index}
                                        >
                                            <div className="bht-detail-field">
                                                <strong>
                                                    {
                                                        c.ColumnName
                                                    }
                                                </strong>

                                                <ChangeBadge
                                                    change={c}
                                                />
                                            </div>

                                            <div className="bht-detail-value old">
                                                <small>
                                                    مقدار قبلی
                                                </small>

                                                <code>
                                                    {val(
                                                        c.OldValue
                                                    )}
                                                </code>
                                            </div>

                                            <div className="bht-detail-arrow">
                                                →
                                            </div>

                                            <div className="bht-detail-value new">
                                                <small>
                                                    مقدار جدید
                                                </small>

                                                <code>
                                                    {val(
                                                        c.NewValue
                                                    )}
                                                </code>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>


                            {(entityInfo ||
                                onOpenRecord) && (
                                <div className="bht-detail-actions">
                                    {onOpenRecord && (
                                        <Button
                                            onClick={() =>
                                                onOpenRecord(
                                                    selected.TableName,
                                                    selected.KeyColumnValue
                                                )
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={
                                                    faExternalLinkAlt
                                                }
                                            />{" "}
                                            باز کردن رکورد
                                            اصلی
                                        </Button>
                                    )}

                                    <Button
                                        variant="outline-secondary"
                                        onClick={() =>
                                            setSelected(
                                                null
                                            )
                                        }
                                    >
                                        بستن
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}
